/* eslint react/display-name: 0 */
/* HACK: eslint considers the `Cell` key from
 * react-table `columns` to be a React Component.
 */
import React, { useMemo, useCallback } from 'react'
import styled, { withTheme } from 'styled-components'
import Spinner from 'react-loader-spinner'
import { useTable, useSortBy, Row } from 'react-table'
import { IconChat, IconCheck } from '@loadsmart/icons'

import Address from 'components/Address'
import Badge from 'components/Badge'
import Equipment from 'components/Equipment'
import Tooltip from 'components/Tooltip'
import formatCurrency from 'utils/formatCurrency'
import { Table, THead, TBody, Tr, Th, Td } from 'components/Table'
import { numberFormatter } from 'utils/numbers'
import { Theme } from 'styles/theme'
import { AwardedCarriersCell } from './AwardedCarriersCell'

const Container = styled.div`
  display: block;
  max-width: 100%;
`
const Wrapper = styled.div`
  display: block;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`
const tableInitalState = { sortBy: [{ id: 'awarded' }] }
const locationSort = (field: string) => (rowA: Row, rowB: Row) => {
  const locationA = rowA.values[field]
  const locationB = rowB.values[field]
  const stringA = `${locationA.state}${locationA.city}${
    locationA.zipcode || ''
  }`.trim()
  const stringB = `${locationB.state}${locationB.city}${
    locationB.zipcode || ''
  }`.trim()

  return stringA > stringB ? 1 : -1
}
const columns = [
  {
    Header: '',
    Cell: withTheme(({ theme, value }: { theme: Theme; value: boolean }) =>
      value ? (
        <IconCheck color={theme.colors.main} height={22} width={22} />
      ) : null
    ),
    accessor: 'awarded',
    className: 'text-center',
  },
  {
    Header: 'Origin',
    Cell: ({
      value,
    }: {
      value: { city: string; state: string; zipcode: string }
    }) => <Address {...value} />,
    accessor: 'origin',
    className: 'text-left',
    sortType: locationSort('origin'),
  },
  {
    Header: 'Destination',
    Cell: ({
      value,
    }: {
      value: { city: string; state: string; zipcode: string }
    }) => <Address {...value} />,
    accessor: 'destination',
    className: 'text-left',
    sortType: locationSort('destination'),
  },
  {
    Header: 'ID',
    accessor: 'display_id',
    className: 'text-left',
  },
  {
    Header: 'Bids',
    Cell: ({ value }: { value: number }) => (
      <Badge variant="light">{value}</Badge>
    ),
    accessor: 'proposalsCount',
    className: 'text-center',
  },
  {
    Header: 'Miles',
    accessor: 'miles',
    className: 'text-center',
  },
  {
    Header: 'Vol',
    accessor: 'volume',
    className: 'text-center',
  },
  {
    Header: 'Equipment',
    accessor: 'equipment_type',
    Cell: ({ value }: { value: string }) => <Equipment equipmentType={value} />,
    className: 'text-center',
  },
  {
    Header: 'Notes',
    accessor: 'notes',
    Cell: ({ value, row }: { value: string; row: { id: string } }) =>
      value ? (
        <div>
          <IconChat
            data-tip
            data-for={`notes-${row.id}`}
            width={20}
            height={20}
          />
          <Tooltip id={`notes-${row.id}`}>{value}</Tooltip>
        </div>
      ) : null,
    className: 'text-center',
  },
  {
    Header: 'Awarded Carriers',
    accessor: 'awardedProposals',
    Cell: AwardedCarriersCell,
    className: 'text-left',
  },
  {
    Header: 'Rate',
    accessor: 'rate',
    className: 'text-left',
  },
]

export default function LanesTable({
  lanes,
  selectedId,
  isLoading = false,
  onSelect,
}: {
  lanes: Lane[]
  selectedId: number | undefined
  isLoading: boolean
  onSelect: (lane: Lane) => void
}) {
  const data = useMemo(
    () =>
      lanes.map((lane: Lane) => {
        const awardedProposals = Object.values(
          lane.awarded_proposals ?? {}
        ).filter(({ awarded_type }) => !!awarded_type)
        const hasAwardedProposal = awardedProposals.length > 0
        return {
          ...lane,
          awarded: hasAwardedProposal,
          awardedProposals,
          miles: numberFormatter(lane.mileage),
          proposalsCount: numberFormatter(lane.proposals_count),
          rate: hasAwardedProposal
            ? formatCurrency(awardedProposals[0].rate_price)
            : '-',
          volume: numberFormatter(lane.volume),
          origin: {
            city: lane.origin_city,
            state: lane.origin_state,
            zipcode: lane.origin_zip,
          },
          destination: {
            city: lane.dest_city,
            state: lane.dest_state,
            zipcode: lane.dest_zip,
          },
        }
      }),
    [lanes]
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
  } = useTable(
    {
      columns,
      data,
      getRowId: (row: Row, relativeIndex: number, parent: Row) =>
        parent
          ? [parent.id, relativeIndex].join('.')
          : (row?.id || relativeIndex).toString(),
      initialState: tableInitalState,
      disableSortRemove: true,
      autoResetSortBy: false,
    } as any,
    useSortBy
  )

  const onClickRow = useCallback(
    (row) => {
      onSelect(row.original)
    },
    [onSelect]
  )

  return (
    <Container>
      <Wrapper>
        <Table {...getTableProps()}>
          <THead>
            {headerGroups.map((headerGroup) => (
              <Tr
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.getHeaderGroupProps().key}
              >
                {headerGroup.headers.map((column: any) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={column.className}
                    key={column.getHeaderProps().key}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? ' ▼'
                          : ' ▲'
                        : ''}
                    </span>
                  </Th>
                ))}
              </Tr>
            ))}
          </THead>
          <TBody {...getTableBodyProps()}>
            {isLoading ? (
              <Tr>
                <Td colSpan={allColumns.length} className="text-center">
                  <Spinner type="ThreeDots" width={24} height={24} />
                </Td>
              </Tr>
            ) : (
              rows.map((row) => {
                prepareRow(row)

                const rowProps = row.getRowProps()
                const onClick = () => onClickRow && onClickRow(row)

                return (
                  <Tr
                    {...rowProps}
                    onClick={onClick}
                    isSelected={selectedId?.toString() === row.id}
                    key={rowProps.key}
                  >
                    {row.cells.map((cell: any) => (
                      <Td
                        {...cell.getCellProps({
                          className: cell.column.className,
                          style: cell.column.style,
                        })}
                        key={cell.getCellProps().key}
                      >
                        {cell.render('Cell')}
                      </Td>
                    ))}
                  </Tr>
                )
              })
            )}
          </TBody>
        </Table>
      </Wrapper>
    </Container>
  )
}
